import axios from "axios";
import Button, { ButtonGroup } from "components/bootstrap/Button";
import FormGroup from "components/bootstrap/forms/FormGroup";
import Input from "components/bootstrap/forms/Input";
import InputGroup from "components/bootstrap/forms/InputGroup";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "components/bootstrap/Modal";
import showNotification from "components/extras/showNotification";
import { useFormik } from "formik";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CompanyService from "services/CompanyService";
import envConfig from '../../../../config';
import DaumPostcode from 'react-daum-postcode';
import { serachAddress } from "lib/map";

/******************************************************************
 * 파일명 : frontend2\src\pages\common\Headers\components\AddCompanyModal.js
 * 설명 : 상단 util 영역 > 설정 모달 > 회사 변경 탭 > 신규 추가 모달
 ******************************************************************/

const MAX_LENGTH = 255;
const REGIS_NUMBER_MAX_LENGTH = 13;

const AddCompanyModal = ({
    isModal,
    setIsModal,
    onCreated
}) => {
    const { t } = useTranslation();
    const [registrationNumberCheck, setRegistrationNumberCheck] = useState(false);
    const [companyType, setCompnayType] = useState('개인 사업자');
    const [isOpenAddress, setIsOpenAddress] = useState(false);


    const validate = async (values) => {
        const errors = {};

        if (!values.name) {
            errors.name = t('회사명을 입력해주세요');
        }

        if (values.name.length > MAX_LENGTH) {
            errors.name = t('common.validation.fieldExceedsCharacters', { fieldName: t('회사명'), maxLength: MAX_LENGTH });
        }

        if (!values.tel) {
            errors.tel = t('회사 연락처를 입력해주세요');
        }

        if (values.tel.length > MAX_LENGTH) {
            errors.tel = t('common.validation.fieldExceedsCharacters', { fieldName: t('회사 전화번호'), maxLength: MAX_LENGTH });
        }

        if(companyType !== '비사업자') {
            if(!registrationNumberCheck) {
                errors.registrationNumber = t('회사 사업자 번호를 체크해주세요');
            }

            if (!values.registrationNumber) {
                errors.registrationNumber = t('회사 사업자 등록번호를 입력해주세요');
            }

            if (values.registrationNumber.length > REGIS_NUMBER_MAX_LENGTH) {
                errors.registrationNumber = t('common.validation.fieldExceedsCharacters', { fieldName: t('사업자 등록번호'), maxLength: REGIS_NUMBER_MAX_LENGTH });
            }
        }

        if(values.name) {
            await CompanyService.search({name: values.name}).then((res) => {
                if(res.data.length > 0) {
                    errors.name = t('이미 등록된 회사입니다.');
                }
            });
        }

        if (!values.address) {
            errors.address = t('회사 주소는 출퇴근을 위해 필수로 입력해주세요');
        }

        if (values.address.length > MAX_LENGTH) {
            errors.address = t('common.validation.fieldExceedsCharacters', { fieldName: t('회사주소'), maxLength: MAX_LENGTH });
        }

        if (values.addressDetail.length > MAX_LENGTH) {
            errors.addressDetail = t('common.validation.fieldExceedsCharacters', { fieldName: t('회사주소'), maxLength: MAX_LENGTH });
        }

        return errors;
    };

    const flexChecks = useFormik({
		initialValues: {
			name: '',
			tel: '',
			companyAnniversary: '',
			registrationNumber: '',
			ceoName: '',
			corporationNumber: '',
			departmentName: '',
			rankName: '',
			address: '',
			addressDetail: '',
			lat: '',
			lng: '',
			logo: '',
		},
		validate,
		onSubmit: async (values) => {
			values.corporationNumber = values.corporationNumber.replace(/-/gi, '');
			values.registrationNumber = values.registrationNumber.replace(/-/gi, '');

			await CompanyService.create(values)
			.then((createdCompany) => {
				showNotification(t('New Add'), t(createdCompany.message), 'info');
				setIsModal(false);
                if (onCreated) onCreated();
			})
			.catch((res) => {
				showNotification(t('New Add'), t('회사 생성에 실패하였습니다'), 'danger');
			})
			.finally(() => {});

		},
	});

    let addr = 'https://api.odcloud.kr/api/nts-businessman/v1/status?serviceKey=';

    const checkCorporateRegiNumberToApi = async (valueObj) => {
		// 법인 사업자가 개인으로 체크하고 들어오는것 방지 (10자리 숫자중 4,5번째 숫자가 8,1~7 이면 법인)
		//buttonFlag = true 개인사업자
		let config = {
			method: 'post',
			url: addr + envConfig.REACT_APP_BUSINESS_REGISTRATION_INFORMATION_API_KEY,
			headers: {
				Authorization: 'Bearer ',
				'Content-Type': 'application/json',
			},
		};

		const k = valueObj.registrationNumber.replace(/-/gi, '');
		//사업자 중복체크
		const res = await CompanyService.checkCorporateRegiNumber(k);
		if (res.data.messageSet == 'danger') {
			showNotification(t('신규 추가'), t(res.data.message), res.data.messageSet);
			return false;
		}

		config.data = JSON.stringify({
			b_no: [k],
		});

		await axios(config).then((res) => {
			if (res.data.data[0].b_stt !== '' && res.data.data[0].b_stt.indexOf('폐업') === -1) {
				showNotification(t('신규 추가'), t(' 사업자 등록이 인증 되었습니다.'), 'info');
				setRegistrationNumberCheck(true);
			} else {
				setCompnayType('비사업자');
				flexChecks.setFieldValue('registrationNumber', '');
				flexChecks.setFieldValue('corporationNumber', '');
				showNotification(t('신규 추가'), t(' 사용 할 수 없는 사업자 입니다.'), 'danger');
			}
		});
	};

    const addAddress = async (data) => {
        let fullAddress = data.address;
        let extraAddress = '';
        try {
            if (data.addressType === 'R') {
                if (data.bname !== '') {
                    extraAddress += data.bname;
                }
                if (data.buildingName !== '') {
                    extraAddress += extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName;
                }
                fullAddress += extraAddress !== '' ? ` (${extraAddress})` : '';
            }
            flexChecks.setFieldValue('address', fullAddress);

            const addressRespons = await serachAddress(fullAddress);
            if (addressRespons.documents) {
                flexChecks.setFieldValue('lat', parseFloat(addressRespons.documents[0].y));
                flexChecks.setFieldValue('lng', parseFloat(addressRespons.documents[0].x));
            }
        } catch (e) {

        } finally {
            setIsOpenAddress(false); // 창닫기
        }
    };

    useEffect(() => {
		if (!isModal) {
			setRegistrationNumberCheck(false);
			flexChecks.resetForm();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isModal]);

    return(
        <>
            <Modal
                isOpen={isModal}
                size='lg'
                setIsOpen={setIsModal}
                isStaticBackdrop={true}
                isCentered={true}
                secondModal={true}
            >
                <form className='g-4 p-2' onSubmit={flexChecks.handleSubmit}>
                    <ModalHeader setIsOpen={setIsModal}>
                        <ModalTitle>{t('신규 추가')}</ModalTitle>
                    </ModalHeader>
                    <ModalBody>
                        <div className='row'>
                            <div className='col-12  pt-3'>
                                <ButtonGroup>
                                    <Button
                                        type='button'
                                        onClick={() => {
                                            //setButtonFlag(true);
                                            setCompnayType('개인 사업자');
                                        }}
                                        disabled={registrationNumberCheck}
                                        color={companyType === '개인 사업자' ? 'info' : 'light'}>
                                        {t('개인 사업자')}
                                    </Button>
                                    <Button
                                        type='button'
                                        onClick={() => {
                                            //setButtonFlag(false);
                                            setCompnayType('법인 사업자');
                                        }}
                                        disabled={registrationNumberCheck}
                                        color={companyType === '법인 사업자' ? 'info' : 'light'}>
                                        {t('법인 사업자')}
                                    </Button>
                                    <Button
                                        type='button'
                                        onClick={() => {
                                            //setButtonFlag(false);
                                            setCompnayType('비사업자');
                                        }}
                                        disabled={registrationNumberCheck}
                                        color={companyType === '비사업자' ? 'info' : 'light'}>
                                        {t('비사업자')}
                                    </Button>
                                </ButtonGroup>
                            </div>

                            <div className='col-12 row pt-3'>
                                <div className='col-12 col-md-6'>
                                    <FormGroup label={t('회사명')}>
                                        <Input
                                            id='company-name'
                                            type='text'
                                            name='name'
                                            onChange={flexChecks.handleChange}
                                            onBlur={flexChecks.handleBlur}
                                            value={flexChecks.values.name}
                                        />
                                    </FormGroup>
                                    {flexChecks.touched.name && flexChecks.errors.name ? (
                                        <div className='text-danger'>
                                            {t(flexChecks.errors.name)}
                                        </div>
                                    ) : null}
                                </div>
                                <div className='col-12 col-md-6'>
                                    <FormGroup label={t('회사 전화번호')}>
                                        <Input
                                            id='company-tel'
                                            type='tel'
                                            name='tel'
                                            onChange={flexChecks.handleChange}
                                            onBlur={flexChecks.handleBlur}
                                            value={flexChecks.values.tel}
                                        />
                                    </FormGroup>
                                    {flexChecks.touched.tel && flexChecks.errors.tel ? (
                                        <div className='text-danger'>
                                            {t(flexChecks.errors.tel)}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {companyType !== '비사업자' && (
                                <div className='col-12 row  pt-3'>
                                    <div className='col-12 col-md-6'>
                                        <FormGroup label={t('사업자 등록번호')}>
                                            <InputGroup>
                                                <Input
                                                    id='company-registration-number'
                                                    type='text'
                                                    name='registrationNumber'
                                                    onChange={flexChecks.handleChange}
                                                    onBlur={flexChecks.handleBlur}
                                                    value={flexChecks.values.registrationNumber}
                                                    placeholder={t('사업자등록번호')}
                                                    disabled={registrationNumberCheck}
                                                />
                                                <Button
                                                    type='button'
                                                    disabled={registrationNumberCheck}
                                                    color='info'
                                                    onClick={() => {
                                                        checkCorporateRegiNumberToApi(
                                                            flexChecks.values,
                                                        );
                                                    }}>
                                                    {t('사업자 등록번호 체크')}
                                                </Button>
                                            </InputGroup>
                                        </FormGroup>
                                        {flexChecks.touched.name && flexChecks.errors.registrationNumber ? (
                                            <div className='text-danger'>
                                                {t(flexChecks.errors.registrationNumber)}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className='col-12 col-md-6'>
                                        {companyType === '법인 사업자' && (
                                            <>
                                            <FormGroup label={t('법인 번호')}>
                                                <Input
                                                    name='corporationNumber'
                                                    placeholder={t('법인등록번호(13자리)')}
                                                    onChange={flexChecks.handleChange}
                                                    onBlur={flexChecks.handleBlur}
                                                    value={flexChecks.values.corporationNumber}
                                                    // disabled={registrationNumberCheck}
                                                />
                                            </FormGroup>
                                            {flexChecks.touched.name && flexChecks.errors.corporationNumber ? (
                                                <div className='text-danger'>
                                                    {t(flexChecks.errors.corporationNumber)}
                                                </div>
                                            ) : null}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className='col-12  pt-3'>
                                <FormGroup label={t('회사주소')}>
                                    <InputGroup>
                                        <Input
                                            id='company-address'
                                            type='text'
                                            name='address'
                                            readOnly={true}
                                            placeholder={''}
                                            onChange={flexChecks.handleChange}
                                            onBlur={flexChecks.handleBlur}
                                            value={flexChecks.values.address}
                                        />
                                        <Button
                                            type='button'
                                            color='info'
                                            onClick={() => {
                                                setIsOpenAddress(true);
                                            }}>
                                            {t('주소 검색')}
                                        </Button>
                                    </InputGroup>
                                    <div className='pt-2'>
                                        <Input
                                            id='company-addressDetail'
                                            type='text'
                                            name='addressDetail'
                                            placeholder={t('상세주소를 입력해주세요')}
                                            onChange={flexChecks.handleChange}
                                            onBlur={flexChecks.handleBlur}
                                            value={flexChecks.values.addressDetail}
                                        />
                                    </div>
                                </FormGroup>
                                {flexChecks.touched.addressDetail && flexChecks.errors.addressDetail ? (
                                    <div className='text-danger'>
                                        {t(flexChecks.errors.addressDetail)}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            isDisable={flexChecks.isSubmitting}
                            color='info'
                            type='submit'>
                            {t('생성')}
                        </Button>
                    </ModalFooter>
                </form>
            </Modal>

            <Modal
                isOpen={isOpenAddress}
                setIsOpen={setIsOpenAddress}
                isStaticBackdrop={true}
                isCentered={true}
                secondModal={true}
            >
                <ModalHeader setIsOpen={setIsOpenAddress}></ModalHeader>
                <ModalBody>
                    <DaumPostcode onComplete={addAddress} style={{ height: '50vh' }} />
                </ModalBody>
            </Modal>
        </>
    )
};

export default AddCompanyModal;